"use client";

// Shared recovery snapshot for `<RelayRecovery>`'s trigger and modal body.
//
// Extracted from the equivalent fan-out inside `RelayWizardStateHost`'s engine
// (which badges the deposit wizard's gear): read the catalog once, then
// GET /v1/recovery per target, and sum the tokens the server flags
// `recoverable`. Kept as its own hook rather than shared with the wizard because
// the two differ in three ways the wizard's version can't express:
//
//   * it runs at the COMPOUND ROOT, not inside the modal -- a badge whose count
//     only appeared once you opened the modal would be pointless, so this fetch
//     deliberately fires for a mounted CLOSED modal;
//   * a catalog with NO targets still gets one lookup (`target: undefined`, the
//     configured default) instead of a flat zero, because that is exactly what
//     `<RelayRecovery.Content>` goes on to inspect in that case;
//   * it needs no wallet capability at all. The count is pure HTTP; whether the
//     connected wallet can actually SIGN a recovery is the modal body's concern
//     (see RelayRecoveryContent's trio gate), not the badge's.
//
// Gated on a resolved, valid recipient and nothing else: a disconnected page
// makes zero requests. No polling either -- a confirmed recovery explicitly
// calls `refresh()`. Rapid opening and closing reuses this snapshot; an open
// refreshes only after the previous attempt is 30 seconds old.
import * as React from "react";

import type { RelayClient } from "../lib/client";
import { countRecoverableTokens, groupForwarders, type RecoveryTargetEntry } from "../lib/recoveryGroups";
import { isAddress } from "../lib/units";

export interface UseRecoverableCountOptions {
  /** Already-resolved client (see resolveRelayApiBaseUrl / createRelayClient). */
  client: RelayClient;
  /** The address to count for. Absent/invalid -- zero, and zero requests. */
  recipient?: string;
}

export interface UseRecoverableCountResult {
  /** Recoverable tokens summed by forwarder; `0` also covers loading/failure. */
  count: number;
  /** The same per-target data consumed by the modal body. */
  targets: readonly RecoveryTargetEntry[];
  /** True only until this recipient's first snapshot settles. */
  loading: boolean;
  /** Snapshot failure, retained so an open modal can report it to the host. */
  error?: Error;
  /** Re-fetch after a recovery transaction changes on-chain state. */
  refresh: () => void;
  /** Re-fetch on open only when the last attempt is old enough. */
  refreshIfStale: () => void;
}

interface RecoverySnapshotState {
  recipientKey?: string;
  targets: readonly RecoveryTargetEntry[];
  badgeFailed?: boolean;
  error?: Error;
}

interface RecoverySnapshotResponse {
  targets: readonly RecoveryTargetEntry[];
  badgeFailed: boolean;
  error?: Error;
}

const EMPTY_TARGETS: readonly RecoveryTargetEntry[] = [];
const RECOVERY_SNAPSHOT_STALE_MS = 30_000;

/**
 * Fetches the recovery data once at the compound root. The trigger reads the
 * aggregate count and the modal body reads `targets`, avoiding a second fan-out
 * on every open. A broken/unreachable backend still degrades to a zero badge.
 */
export function useRecoverableCount({
  client,
  recipient,
}: UseRecoverableCountOptions): UseRecoverableCountResult {
  const [snapshot, setSnapshot] = React.useState<RecoverySnapshotState>({
    targets: EMPTY_TARGETS,
  });
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const inFlightRef = React.useRef<
    | {
        client: RelayClient;
        recipientKey: string;
        generation: number;
        promise: Promise<RecoverySnapshotResponse>;
      }
    | undefined
  >(undefined);
  const lastAttemptRef = React.useRef<
    | {
        client: RelayClient;
        recipientKey: string;
        at: number;
      }
    | undefined
  >(undefined);

  const recipientKey = recipient && isAddress(recipient) ? recipient.toLowerCase() : undefined;

  React.useEffect(() => {
    if (!recipientKey) {
      setSnapshot({ targets: EMPTY_TARGETS });
      return;
    }

    let cancelled = false;
    let pending = inFlightRef.current;
    if (
      !pending ||
      pending.client !== client ||
      pending.recipientKey !== recipientKey ||
      pending.generation !== refreshNonce
    ) {
      const promise = (async (): Promise<RecoverySnapshotResponse> => {
        let catalogError: Error | undefined;
        let targets = [] as Awaited<ReturnType<RelayClient["getConfig"]>>["targets"];
        try {
          targets = (await client.getConfig()).targets;
        } catch (err) {
          // Preserve the old modal fallback: if the catalog is unavailable,
          // inspect the backend's configured default target once.
          catalogError = err instanceof Error ? err : new Error(String(err));
        }

        // No targets (or an older/unavailable catalog): one lookup against the
        // configured default, mirroring the pre-multi-target panel behavior.
        const lookups: Array<() => ReturnType<RelayClient["getRecovery"]>> =
          targets.length === 0
            ? [() => client.getRecovery({ user: recipientKey })]
            : targets.map(
                (t) => () => client.getRecovery({ user: recipientKey, target: t.symbol }),
              );
        // Per-target failures do not erase successful targets, but are retained
        // for the open modal's error callback instead of disappearing silently.
        const results = await Promise.all(
          lookups.map(async (lookup) => {
            try {
              return { info: await lookup() };
            } catch (err) {
              return { error: err instanceof Error ? err : new Error(String(err)) };
            }
          }),
        );
        const entries: RecoveryTargetEntry[] =
          targets.length === 0
            ? results[0]?.info
              ? [{ symbol: "", isDefault: true, info: results[0].info }]
              : []
            : targets.flatMap((t, i) =>
                results[i]?.info
                  ? [{ symbol: t.symbol, isDefault: t.isDefault, info: results[i].info }]
                  : [],
              );
        const recoveryError = results.find((result) => result.error)?.error;
        return {
          targets: entries,
          badgeFailed: Boolean(catalogError || (recoveryError && entries.length === 0)),
          error: catalogError ?? recoveryError,
        };
      })().catch((err): RecoverySnapshotResponse => ({
        targets: EMPTY_TARGETS,
        badgeFailed: true,
        error: err instanceof Error ? err : new Error(String(err)),
      }));
      pending = { client, recipientKey, generation: refreshNonce, promise };
      inFlightRef.current = pending;
      void promise.finally(() => {
        lastAttemptRef.current = {
          client,
          recipientKey,
          at: Date.now(),
        };
        if (inFlightRef.current?.promise === promise) inFlightRef.current = undefined;
      });
    }

    void pending.promise.then((result) => {
      if (cancelled) return;
      setSnapshot({
        recipientKey,
        targets: result.targets,
        badgeFailed: result.badgeFailed,
        error: result.error,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [client, recipientKey, refreshNonce]);

  const refresh = React.useCallback(() => setRefreshNonce((value) => value + 1), []);
  const refreshIfStale = React.useCallback(() => {
    if (!recipientKey) return;
    const pending = inFlightRef.current;
    if (
      pending?.client === client &&
      pending.recipientKey === recipientKey
    ) {
      return;
    }
    const lastAttempt = lastAttemptRef.current;
    if (
      lastAttempt?.client === client &&
      lastAttempt.recipientKey === recipientKey &&
      Date.now() - lastAttempt.at < RECOVERY_SNAPSHOT_STALE_MS
    ) {
      return;
    }
    setRefreshNonce((value) => value + 1);
  }, [client, recipientKey]);
  const targets =
    recipientKey && snapshot.recipientKey === recipientKey
      ? snapshot.targets
      : EMPTY_TARGETS;
  const error =
    recipientKey && snapshot.recipientKey === recipientKey
      ? snapshot.error
      : undefined;
  const loading = Boolean(
    recipientKey && snapshot.recipientKey !== recipientKey,
  );
  const badgeFailed = Boolean(
    recipientKey && snapshot.recipientKey === recipientKey && snapshot.badgeFailed,
  );

  return React.useMemo(
    () => ({
      // A catalog failure still gives the open modal one default-target
      // fallback, but the closed trigger keeps its established fail-soft `0`.
      count: badgeFailed ? 0 : countRecoverableTokens(groupForwarders(targets)),
      targets,
      loading,
      error,
      refresh,
      refreshIfStale,
    }),
    [targets, loading, badgeFailed, error, refresh, refreshIfStale],
  );
}
